import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "@/lib/api";
import { STAGE_LABELS, inrShort, label } from "@/lib/constants";

const COLORS = { AWARENESS: "#D9D9D9", ENGAGEMENT: "#9FD9C4", DISCOVERY: "#F5E04F", PROPOSAL: "#F7A934", WON: "#1F9E7A", LOST: "#E8776B" };
const VARIANT = {
  light: "border-neutral-200 bg-white text-[#151515]",
  teal: "border-[#1F9E7A] bg-[#1F9E7A] text-white",
  dark: "border-[#232323] bg-[#232323] text-[#F5E04F]",
  yellow: "border-[#F5E04F] bg-[#F5E04F] text-[#151515]",
};
const Kpi = ({ t, v, sub, testId, variant = "light" }) => (
  <div className={`rounded-3xl border p-5 ${VARIANT[variant]}`}>
    <p className="text-xs font-medium opacity-80">{t}</p>
    <p data-testid={testId} className="mt-2 font-mono text-3xl font-semibold tabular-nums">{v}</p>
    {sub && <p className="mt-1 text-xs opacity-70">{sub}</p>}
  </div>
);
const Card = ({ title, children, dark }) => <div className={`rounded-3xl border p-5 ${dark ? "border-[#232323] bg-[#232323] text-white" : "border-neutral-200 bg-white"}`}><h3 className={`font-display mb-4 text-base font-semibold ${dark ? "text-white" : "text-[#1A1A1A]"}`}>{title}</h3>{children}</div>;

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-neutral-400">Loading…</p>;
  const mm = d.meeting_metrics;
  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-2xl font-bold text-[#151515]">Insights</h1><p className="text-sm text-neutral-500">Meeting efficiency & pipeline health across verticals.</p></div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi t="Meeting Completion Rate" v={`${mm.mcr}%`} testId="kpi-mcr" variant="teal" sub={`${mm.n_completed} done / (${mm.n_scheduled} sched − ${mm.n_cancelled} cancelled)`} />
        <Kpi t="Meeting Conversion Eff." v={`${mm.mce}%`} testId="kpi-mce" sub={`${mm.n_converted} unique wins / ${mm.n_completed} completed`} />
        <Kpi t="Qualified Pipeline" v={inrShort(d.tracks.project.qualified_pipeline_value)} testId="kpi-qualified-pipeline" variant="dark" sub={`Proposal→Win ${d.tracks.project.proposal_to_win}%`} />
        <Kpi t="Retainer MRR" v={inrShort(d.tracks.retainer.mrr)} testId="kpi-mrr" variant="yellow" sub={`LTV ≈ ${inrShort(d.tracks.retainer.ltv)} (12-mo)`} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Macro lifecycle funnel" dark>
          <div className="h-64" data-testid="funnel-chart">
            <ResponsiveContainer><BarChart data={d.funnel.map((f) => ({ ...f, name: STAGE_LABELS[f.stage].split(" ")[0] }))}>
              <XAxis dataKey="name" stroke="#a3a3a3" fontSize={11} /><YAxis stroke="#a3a3a3" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 12, color: "#151515" }} cursor={{ fill: "#ffffff15" }} />
              <Bar dataKey="count" radius={[8, 8, 8, 8]}>{d.funnel.map((f) => <Cell key={f.stage} fill={COLORS[f.stage]} />)}</Bar>
            </BarChart></ResponsiveContainer>
          </div>
        </Card>
        <Card title="Meeting state distribution">
          <div className="space-y-2" data-testid="meeting-status-breakdown">
            {Object.entries(mm.by_status).map(([k, v]) => {
              const total = Object.values(mm.by_status).reduce((a, b) => a + b, 0) || 1;
              return (<div key={k}><div className="flex justify-between text-xs text-neutral-600"><span>{label(k)}</span><span className="font-mono">{v}</span></div>
                <div className="mt-1 h-2 rounded bg-neutral-100"><div className="h-2 rounded bg-[#1F9E7A]" style={{ width: `${(v / total) * 100}%` }} /></div></div>);
            })}
          </div>
        </Card>
      </div>
      <Card title="Service verticals">
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="service-table">
          <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-neutral-400"><th className="py-2">Service</th><th>Track</th><th>Leads</th><th>Active</th><th>Pipeline</th><th>Won</th><th>Won value</th><th>Prop→Win</th></tr></thead>
          <tbody>{d.services.map((s) => (
            <tr key={s.service} className="border-t border-neutral-200 text-neutral-600"><td className="py-2.5 font-medium text-[#1A1A1A]">{s.label}</td><td><span className={s.track === "RETAINER" ? "text-amber-700" : "text-[#1F9E7A]"}>{s.track}</span></td>
              <td className="font-mono">{s.leads}</td><td className="font-mono">{s.active}</td><td className="font-mono">{inrShort(s.pipeline_value)}</td><td className="font-mono">{s.won}</td><td className="font-mono">{inrShort(s.won_value)}</td><td className="font-mono">{s.proposal_to_win}%</td></tr>))}
          </tbody></table></div>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        {d.reps.length > 0 && (
          <Card title="Team performance">
            <div className="space-y-2" data-testid="rep-table">{d.reps.map((r) => (
              <div key={r.user_id} className="grid grid-cols-5 items-center gap-2 border-b border-neutral-200 pb-2 text-xs text-neutral-600">
                <span className="col-span-2 font-medium text-[#1A1A1A]">{r.name}</span><span>{r.active} active</span><span>{r.meetings_completed} met</span>
                <span className={r.pending_dispositions ? "text-rose-700" : "text-emerald-700"}>{r.won} won · {r.pending_dispositions} pend.</span>
              </div>))}</div>
          </Card>
        )}
        <Card title="Loss reasons">
          {d.loss_reasons.length === 0 ? <p className="text-sm text-neutral-400">No disqualified leads.</p> : d.loss_reasons.map((l) => (
            <div key={l.reason} className="flex justify-between border-b border-neutral-200 py-2 text-sm text-neutral-600"><span>{l.reason}</span><span className="font-mono text-rose-700">{l.count}</span></div>))}
        </Card>
      </div>
    </div>
  );
}
